// @ts-check
// File: app/src/utils/transitionInjector.js
//
// Post-LLM normalisation step that inserts `Rest` nodes between adjacent
// siblings whose effective bordering exercises differ. Runs after
// `normalizeUnits` and before `validateAndAdjustTiming` in the LLM
// generation pipeline (apis/llmApi.js). LLMs no longer author transition
// nodes — the injector is the single source of truth.
//
// Two-tier behaviour (decided per boundary):
//   • Inter-block boundaries get a longer "Setup" transition. These are
//     root-child (phase) boundaries AND station changes between two sibling
//     Group blocks (supersets / circuits) nested anywhere in the tree. Plan
//     view renders these as visible chapter-break dividers regardless of
//     duration. (Issue #30: supersets live one level below root, so without
//     the Group↔Group rule the gap between them collapsed to a hidden nudge.)
//   • Intra-block boundaries (leaf ↔ leaf, e.g. two exercises inside one
//     circuit) get a short "Transition" that the plan view hides under
//     SUMMARY_TRANSITION_HIDE_SECONDS.
//
// Pure module — does not mutate the input tree, no React imports.
//
// Design notes:
//   • Idempotent. Re-running on an already-injected plan is a no-op because
//     the injector skips boundaries where any `Rest` already sits.
//   • Respects `Rest` with `value: 0` as an explicit zero-rest superset marker
//     (the renderer hides these from summary; the runtime ticks them as a
//     no-op).
//   • Looks through nested Groups: when sibling A is a Group, the injector
//     compares the *last work leaf reachable from A* with the *first work
//     leaf reachable from B*, so transitions land between sibling Groups
//     (e.g. Chest Circuit → Core Finisher) as well as between sibling Works.

import {
  isRecoveryType,
  isWorkLike,
  getExerciseKey,
  getFirstWorkLeaf,
  getLastWorkLeaf,
} from "./planNodeUtils";
import { isInjectedChangeover, isSetupNode } from "./planVisuals";
import { genStableId } from "./planEditing";
import { exerciseOf } from "../config/exercises";

/** @typedef {import("../types/plan").PlanNode} PlanNode */

let _idCounter = 0;
const uid = () => `txn-${++_idCounter}`;

const makeNode = (seconds, label) => ({
  id: uid(),
  type: "Transition",
  label,
  value: Math.max(0, Math.round(Number(seconds) || 0)),
  unit: "s",
  autoStart: true,
});

// A boundary deserves the longer "Setup" block treatment — dedicated time to
// move to a new station and rack equipment — when it is either:
//   • a root-child (phase) boundary (Warm-up → Main → Cool-down), or
//   • a station change between two sibling Group blocks (supersets / circuits)
//     nested anywhere in the tree.
// Everything else (leaf ↔ leaf, e.g. two exercises inside one circuit) gets the
// short, summary-hidden "Transition". Issue #30: supersets sit one level below
// root, so this is what promotes the gap between them from a hidden 15s nudge
// to a visible Setup block.
const isBlockBoundary = (curr, next, rootLevel) =>
  rootLevel || (curr.type === "Group" && next.type === "Group");

// Two leaves are the same exercise when their labels match or the library
// reads both as one entry: "Easy row" into "Row intervals" stays on the rower,
// so there is nothing to set up between them.
const sameExercise = (lhs, rhs) => {
  const lhsKey = getExerciseKey(lhs);
  const rhsKey = getExerciseKey(rhs);
  if (!lhsKey || !rhsKey) return false;
  if (lhsKey === rhsKey) return true;
  const a = exerciseOf(lhs);
  return !!a && a === exerciseOf(rhs);
};

// The changeover a boundary between two adjacent siblings calls for, or null
// when it needs none. The block-vs-transition decision is made per boundary
// (see isBlockBoundary) so a single sibling list can mix Setup blocks (between
// superset Groups) and short Transitions (between leaf exercises). Shared by the
// first pass (injectTransitions) and the re-layout after an edit
// (reflowChangeovers), so the two can never disagree about where one goes.
const changeoverAt = (curr, next, { seconds, blockSeconds, rootLevel }) => {
  // Skip if either side is a recovery node — the injector only fills gaps
  // between active siblings. Recovery nodes already author the gap.
  if (isRecoveryType(curr.type) || isRecoveryType(next.type)) return null;
  if (!isWorkLike(curr) || !isWorkLike(next)) return null;
  // Linked steps run straight on with one Done (#177): no changeover.
  if (next.linkToPrevious) return null;
  const lhs = getLastWorkLeaf(curr);
  const rhs = getFirstWorkLeaf(next);
  if (!lhs || !rhs) return null;
  // Same exercise on both sides of the boundary — no changeover needed.
  if (sameExercise(lhs, rhs)) return null;
  // Block boundaries want the longer Setup, but when blockSeconds isn't
  // configured (0) they fall back to the short Transition so the gap is still
  // marked. Leaf boundaries always use the short Transition.
  const block = isBlockBoundary(curr, next, rootLevel) && blockSeconds > 0;
  const effSeconds = block ? blockSeconds : seconds;
  if (!effSeconds || effSeconds <= 0) return null;
  return { block, seconds: effSeconds, label: block ? "Setup" : "Transition" };
};

// Walk a list of siblings and return a new array with changeover nodes inserted
// between adjacent work-like nodes whose effective bordering exercises differ.
const injectBetweenSiblings = (children, { seconds, blockSeconds, rootLevel }) => {
  if (!Array.isArray(children) || children.length < 2) return children;
  if ((!seconds || seconds <= 0) && (!blockSeconds || blockSeconds <= 0)) {
    return children;
  }
  const out = [];
  for (let i = 0; i < children.length; i++) {
    const curr = children[i];
    out.push(curr);
    const next = children[i + 1];
    if (!next) continue;
    const want = changeoverAt(curr, next, { seconds, blockSeconds, rootLevel });
    if (want) out.push(makeNode(want.seconds, want.label));
  }
  return out;
};

const walk = (node, seconds, blockSeconds, isRoot) => {
  if (!node || typeof node !== "object") return node;
  if (!Array.isArray(node.children)) return node;
  // A time-capped container (AMRAP cap, EMOM window — issue #36/#63) runs as a
  // single self-paced countdown: its children are a checklist the athlete
  // cycles at their own pace, not a sequence of phases with gaps between them.
  // There is no changeover to insert, and the compiler never emits one from in
  // here anyway (it does not recurse into a time-capped Group).
  if (node.type === "Group" && ["s", "m"].includes(node.unit)) return node;
  const recursed = node.children.map((child) =>
    walk(child, seconds, blockSeconds, false),
  );
  // The Setup-vs-Transition choice is made per boundary inside
  // injectBetweenSiblings (root-child phase boundaries and sibling Group↔Group
  // station changes get "Setup"; leaf↔leaf gets "Transition"). When blockSeconds
  // isn't configured the block branch falls back to no insertion, leaving the
  // short transition to cover leaf boundaries.
  const withTransitions = injectBetweenSiblings(recursed, {
    seconds,
    blockSeconds: Number.isFinite(blockSeconds) ? blockSeconds : 0,
    rootLevel: isRoot,
  });
  // Preserve reference equality when nothing changed at this level.
  const unchanged =
    withTransitions.length === node.children.length &&
    withTransitions.every((c, i) => c === node.children[i]);
  if (unchanged) return node;
  return { ...node, children: withTransitions };
};

/**
 * Inject transition `Rest` nodes between adjacent active siblings.
 *
 * @param {PlanNode} plan  Root workout-plan node.
 * @param {{ seconds?: number, blockSeconds?: number }} options
 *   `seconds` (default 15) is the intra-block transition; `blockSeconds`
 *   (default 45) the inter-block setup at root-child boundaries, 0 to disable.
 * @returns {PlanNode} A new plan tree with transitions inserted (or the input
 *                   unchanged if no insertions were needed).
 */
export const injectTransitions = (
  plan,
  { seconds = 15, blockSeconds = 45 } = {},
) => {
  if (!plan) return plan;
  _idCounter = 0;
  const resolvedSeconds = Math.max(0, Math.round(Number(seconds) || 0));
  const resolvedBlock = Math.max(0, Math.round(Number(blockSeconds) || 0));
  if (resolvedSeconds === 0 && resolvedBlock === 0) return plan;
  return walk(plan, resolvedSeconds, resolvedBlock, true);
};

// Re-lay a sibling list's changeovers around its current order. Each existing
// changeover belongs to the step AFTER it (the Setup before a superset is that
// superset's), so it travels with that step when blocks move, and keeps its
// id and its edited length. A boundary that now needs one and has none gets a
// fresh node at the plan's baked length; one whose boundary is gone is dropped.
// `skipSetup` on a step is the user saying "no Setup before this one".
const reflowSiblings = (children, opts) => {
  const owned = new Map();
  const steps = [];
  let pending = [];
  for (const child of children) {
    if (isInjectedChangeover(child)) {
      pending.push(child);
      continue;
    }
    // Stacked changeovers (left behind by an edit made before this pass
    // existed) collapse to the one nearest the step.
    if (pending.length && child.id != null) {
      owned.set(child.id, pending[pending.length - 1]);
    }
    pending = [];
    steps.push(child);
  }

  const out = [];
  for (let i = 0; i < steps.length; i++) {
    let step = steps[i];
    const prev = steps[i - 1];
    const want = prev ? changeoverAt(prev, step, opts) : null;
    // A skip only means something where a Setup would go; clear it elsewhere
    // so a block moved to the top doesn't carry a skip nobody can see.
    if (step.skipSetup && !want?.block) {
      const { skipSetup, ...rest } = step;
      step = rest;
    }
    if (want && !(want.block && step.skipSetup)) {
      const mine = step.id != null ? owned.get(step.id) : null;
      if (mine && isSetupNode(mine) === want.block) out.push(mine);
      else out.push({ ...makeNode(want.seconds, want.label), id: genStableId() });
    }
    out.push(step);
  }
  return out;
};

const reflowWalk = (node, opts, isRoot) => {
  if (!node || typeof node !== "object") return node;
  if (!Array.isArray(node.children)) return node;
  if (node.type === "Group" && ["s", "m"].includes(node.unit)) return node;
  const recursed = node.children.map((child) => reflowWalk(child, opts, false));
  const next = reflowSiblings(recursed, { ...opts, rootLevel: isRoot });
  const unchanged =
    next.length === node.children.length &&
    next.every((c, i) => c === node.children[i]);
  return unchanged ? node : { ...node, children: next };
};

/**
 * Put a plan's changeovers back where its current shape needs them, after an
 * edit has moved, added or deleted steps. Only plans the injector laid out
 * (stamped with `meta.blockSetupSeconds` by finalisePlan) are touched: a plan
 * built by hand, or a Free training checklist, never had them and doesn't
 * grow them. Returns the input unchanged when nothing moved.
 *
 * @param {PlanNode} plan
 * @returns {PlanNode}
 */
export const reflowChangeovers = (plan) => {
  const meta = plan?.meta;
  if (!plan || meta?.blockSetupSeconds == null) return plan;
  const blockSeconds = Math.max(0, Math.round(Number(meta.blockSetupSeconds) || 0));
  const seconds = Number.isFinite(Number(meta.transitionSeconds))
    ? Math.max(0, Math.round(Number(meta.transitionSeconds)))
    : 15;
  return reflowWalk(plan, { seconds, blockSeconds }, true);
};

export default injectTransitions;
